import { Link } from "react-router-dom";
import { Lock, Eye, Clock, CheckCircle2, ShieldQuestion, Trophy, Flame, Activity, Zap } from "lucide-react";
import { SimBadge } from "./Badges";

const MATCH_STATUS_STYLE = {
  SCHEDULED: "bg-slate-800 text-slate-300 border-slate-600",
  SUBMISSION_OPEN: "bg-emerald-950/70 text-emerald-400 border-emerald-500/50",
  SUBMISSION_IN_PROGRESS: "bg-emerald-950/70 text-emerald-400 border-emerald-500/50",
  SUBMISSION_CLOSED: "bg-[#140f02]/70 text-[#F0C72A] border-[#E5B31E]/50",
  INCOMPLETE_TEAM_SUBMISSION: "bg-amber-950/60 text-amber-400 border-amber-500/50",
  CANCELLED: "bg-rose-950/70 text-rose-400 border-rose-500/50",
};

export function MatchStatusBadge({ status }) {
  const s = MATCH_STATUS_STYLE[status] || MATCH_STATUS_STYLE.SCHEDULED;
  return (
    <span data-testid={`match-status-${status}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-mono font-bold uppercase tracking-wider border ${s}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

export function ExhibitionBadge({ className = "" }) {
  return (
    <span data-testid="exhibition-badge" className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-mono font-bold uppercase tracking-wider border bg-fuchsia-950/60 text-fuchsia-300 border-fuchsia-500/50 ${className}`}>
      Exhibition
    </span>
  );
}

// Potential team points — neutral wording (never "better coupon")
export function pointsInfo(total) {
  const t = parseFloat(total);
  if (isNaN(t)) return null;
  if (t < 4.0) return { pts: 1, label: "+1 · lower prediction risk", tone: "text-[#F0C72A] border-[#E5B31E]/40 bg-[#140f02]/40" };
  if (t < 5.5) return { pts: 2, label: "+2 · balanced value / risk", tone: "text-amber-300 border-amber-500/40 bg-amber-950/40" };
  return { pts: 3, label: "+3 · higher potential value, higher prediction risk", tone: "text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-950/40" };
}

export function PointsPill({ total, points, className = "" }) {
  const info = pointsInfo(total);
  if (!info) return null;
  const pts = points ?? info.pts;
  return (
    <span data-testid="points-pill" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${info.tone} ${className}`}>
      POTENTIAL +{pts} · {info.label.replace(/^\+\d · /, "")}
    </span>
  );
}

const DUEL_ICON = {
  WAITING_FOR_COUPONS: Clock,
  WAITING_FOR_PAIR: Clock,
  PAIRED: Lock,
  LOCKED: Lock,
  REVEALED: Eye,
};

const SEL_RESULT_STYLE = {
  WON: "text-emerald-400 border-emerald-500/40",
  LOST: "text-rose-400 border-rose-500/40",
  VOID: "text-slate-400 border-slate-600",
  PENDING: "text-amber-400 border-amber-500/40",
};

function SelResult({ status }) {
  const s = SEL_RESULT_STYLE[status] || SEL_RESULT_STYLE.PENDING;
  return (
    <span data-testid={`sel-result-${status}`} className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase border ${s}`}>
      {status}
    </span>
  );
}

function SealedBox({ coupon, label }) {
  if (!coupon)
    return (
      <div className="flex-1 rounded-lg border border-dashed border-[#2b2512] p-3 text-center text-slate-600 text-xs font-mono uppercase">
        {label} · empty
      </div>
    );
  if (!coupon.content_visible)
    return (
      <div data-testid="sealed-coupon" className="flex-1 rounded-lg border border-[#2b2512] bg-[#070707] p-3">
        <div className="flex items-center gap-1.5 text-slate-500 text-[11px] font-mono uppercase">
          <ShieldQuestion className="w-3.5 h-3.5" /> Sealed · blind until paired
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-slate-300 text-sm truncate">
          {coupon.player?.display_name}
          {coupon.player?.is_simulated && <SimBadge />}
        </div>
      </div>
    );
  return (
    <div data-testid="revealed-coupon" className="flex-1 rounded-lg border border-[#2b2512] bg-[#0d0d0d] p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-slate-200 text-sm truncate">
          {coupon.player?.display_name}
          {coupon.player?.is_simulated && <SimBadge />}
        </div>
        <span className="font-mono text-[#E5B842] font-bold text-sm">{coupon.total_odds}</span>
      </div>
      <ul className="mt-2 space-y-1">
        {coupon.selections?.map((s, i) => (
          <li key={i} className="text-[11px] text-slate-400 flex justify-between gap-2 items-center">
            <span className="truncate">{s.event_label} · {s.market_name} · {s.label}</span>
            <span className="flex items-center gap-1.5 shrink-0">
              {s.result_status && <SelResult status={s.result_status} />}
              <span className="font-mono text-slate-300">{s.odds}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DuelRow({ slot }) {
  const Icon = DUEL_ICON[slot.status] || Clock;
  const resolved = slot.status === "RESOLVED";
  const homeWin = slot.winner_side === "HOME";
  const awayWin = slot.winner_side === "AWAY";
  return (
    <div data-testid={`duel-slot-${slot.sequence_number}`} className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="font-heading font-black text-[#E5B842] w-8 text-lg">#{slot.sequence_number}</span>
          <span className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-400">
            <Icon className="w-3.5 h-3.5" /> {slot.status.replace(/_/g, " ")}
          </span>
        </div>
        {resolved && (
          <span data-testid={`duel-outcome-${slot.sequence_number}`} className="text-[11px] font-mono font-bold uppercase">
            {slot.split ? (
              <span className="text-slate-400">SPLIT · 0 pts</span>
            ) : (
              <span className="text-emerald-400">{slot.winner_side} · <span className="text-[#E5B842]">{slot.winning_total_odds}</span> → +{slot.team_points} · lost {slot.home_lost}-{slot.away_lost}</span>
            )}
          </span>
        )}
      </div>
      <div className="flex gap-2 items-stretch">
        <div className={`flex-1 flex ${homeWin ? "ring-1 ring-emerald-500/50 rounded-lg" : ""}`}><SealedBox coupon={slot.home} label="Home" /></div>
        <div className="self-center text-slate-600 font-heading font-black text-xs">VS</div>
        <div className={`flex-1 flex ${awayWin ? "ring-1 ring-emerald-500/50 rounded-lg" : ""}`}><SealedBox coupon={slot.away} label="Away" /></div>
      </div>
    </div>
  );
}

export function MatchResultCard({ match }) {
  const mr = match.match_result;
  if (!mr) return null;
  const fm = match.final_metrics || {};
  const tc = mr.top_contributor || fm.top_contributor;
  const hw = mr.highest_winning_coupon || fm.highest_winning_coupon;
  const winnerName = mr.result_type === "HOME_WIN" ? match.home_club?.name
    : mr.result_type === "AWAY_WIN" ? match.away_club?.name : null;
  return (
    <div data-testid="match-result-card" className="rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-6 mb-6">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2 text-center">FINAL</div>
      <div className="flex items-center justify-center gap-6">
        <span className="font-heading text-xl font-bold uppercase text-slate-50 text-right flex-1 truncate">{match.home_club?.name}</span>
        <span data-testid="final-score" className="font-heading text-5xl font-black text-[#E5B842] tabular-nums">{mr.home_team_points}–{mr.away_team_points}</span>
        <span className="font-heading text-xl font-bold uppercase text-slate-50 text-left flex-1 truncate">{match.away_club?.name}</span>
      </div>
      <div className="mt-3 text-sm text-slate-400 font-mono text-center">
        Duels Won {mr.home_duels_won}–{mr.away_duels_won} · Team Points {mr.home_team_points}–{mr.away_team_points}
        {winnerName ? ` — ${winnerName} wins on points` : " — League draw"}
      </div>

      {(tc || hw) && (
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          {tc && (
            <div data-testid="top-contributor" className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1">
                <Flame className="w-3.5 h-3.5 text-[#E5B842]" /> Top Contributor
              </div>
              <div className="flex items-center gap-2 text-slate-50 font-heading text-lg font-bold truncate">
                {tc.player?.display_name || "—"}
                {tc.player?.is_simulated && <SimBadge />}
              </div>
              <div className="text-xs font-mono text-slate-400 mt-0.5">
                +{tc.team_points} team points · {tc.duels_won} {tc.duels_won === 1 ? "duel" : "duels"} won · {tc.side}
              </div>
            </div>
          )}
          {hw && (
            <div data-testid="highest-winning-coupon" className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1">
                <Zap className="w-3.5 h-3.5 text-fuchsia-400" /> Highest Winning Coupon
              </div>
              <div className="flex items-center gap-2 text-slate-50 font-heading text-lg font-bold">
                <span className="text-[#E5B842] tabular-nums">{hw.winning_total_odds}</span>
                <span className="text-slate-500 text-sm font-mono">total odds</span>
              </div>
              <div className="text-xs font-mono text-slate-400 mt-0.5 truncate">
                {hw.player?.display_name || "—"} · duel #{hw.sequence_number} · +{hw.team_points} · {hw.selection_count} sel.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Phase 4: Live Match Center ------------------------------------------
export function LiveScoreHeader({ match }) {
  const s = match.live || {};
  const resolved = s.resolved_count ?? 0;
  const total = s.total_duels ?? 11;
  const pct = Math.round((resolved / total) * 100);
  const homeLead = s.leader === "HOME";
  const awayLead = s.leader === "AWAY";
  return (
    <div data-testid="live-score-header" className="rounded-2xl bg-[#070707] border border-[#2b2512] p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <span className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-emerald-400">
          <Activity className="w-3.5 h-3.5" /> Live Score
        </span>
        <span data-testid="resolved-counter" className="font-mono text-xs text-slate-400">
          {resolved}/{total} duels resolved
        </span>
      </div>
      <div className="flex items-center justify-center gap-5 sm:gap-10">
        <div className="flex-1 text-right">
          <div className={`font-heading text-base sm:text-lg font-bold uppercase truncate ${homeLead ? "text-[#E5B842]" : "text-slate-300"}`}>{match.home_club?.short_name || match.home_club?.name}</div>
          <div data-testid="home-live-points" className={`font-heading text-5xl font-black tabular-nums ${homeLead ? "text-[#E5B842]" : "text-slate-100"}`}>{s.home_team_points ?? 0}</div>
          <div className="text-[10px] font-mono text-slate-500">won {s.home_duels_won ?? 0} · max {s.home_max_potential ?? 0}</div>
        </div>
        <div className="text-slate-600 font-heading font-black text-2xl">–</div>
        <div className="flex-1 text-left">
          <div className={`font-heading text-base sm:text-lg font-bold uppercase truncate ${awayLead ? "text-[#E5B842]" : "text-slate-300"}`}>{match.away_club?.short_name || match.away_club?.name}</div>
          <div data-testid="away-live-points" className={`font-heading text-5xl font-black tabular-nums ${awayLead ? "text-[#E5B842]" : "text-slate-100"}`}>{s.away_team_points ?? 0}</div>
          <div className="text-[10px] font-mono text-slate-500">won {s.away_duels_won ?? 0} · max {s.away_max_potential ?? 0}</div>
        </div>
      </div>
      <div className="mt-4 h-1.5 rounded-full bg-[#161616] overflow-hidden">
        <div className="h-full bg-[#E5B842]" style={{ width: `${pct}%`, transition: "width 0.4s ease" }} />
      </div>
      {s.remaining > 0 && (
        <div className="mt-2 text-center text-[10px] font-mono text-slate-500 uppercase tracking-wider">
          {s.remaining} {s.remaining === 1 ? "duel" : "duels"} remaining · up to +{s.max_additional} still in play per team
        </div>
      )}
    </div>
  );
}

export function ClinchBanner({ match }) {
  const s = match.live || {};
  if (!s.clinched_side || s.is_final) return null;
  const club = s.clinched_side === "HOME" ? match.home_club : match.away_club;
  return (
    <div data-testid="clinch-banner" className="rounded-xl border border-emerald-500/50 bg-emerald-950/50 p-4 mb-6 flex items-center justify-center gap-3 pulse-valid">
      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
      <span className="font-heading font-black uppercase tracking-wider text-emerald-300 text-lg">
        Winner Confirmed · {club?.name}
      </span>
    </div>
  );
}

const TIMELINE_ICON = {
  SCHEDULED: Clock,
  STATUS: Activity,
  COUPON_ACCEPTED: CheckCircle2,
  DUEL_REVEALED: Eye,
  DUEL_RESOLVED: Trophy,
  MATCH_FINAL: Flame,
};
const TIMELINE_TINT = {
  DUEL_RESOLVED: "text-emerald-400",
  MATCH_FINAL: "text-[#E5B842]",
  DUEL_REVEALED: "text-[#E5B31E]",
  STATUS: "text-slate-400",
  COUPON_ACCEPTED: "text-slate-500",
  SCHEDULED: "text-slate-400",
};

function clock(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return ""; }
}

export function MatchTimeline({ timeline }) {
  const items = (timeline || []).slice().reverse();
  return (
    <div data-testid="match-timeline" className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4">
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase tracking-tight text-slate-50 mb-3">
        <Activity className="w-4 h-4 text-[#E5B842]" /> Match Timeline
      </h3>
      {items.length === 0 ? (
        <p className="text-slate-500 text-sm font-mono">No events yet.</p>
      ) : (
        <ul className="space-y-0 max-h-[460px] overflow-y-auto">
          {items.map((t) => {
            const Icon = TIMELINE_ICON[t.kind] || Activity;
            const tint = TIMELINE_TINT[t.kind] || "text-slate-400";
            return (
              <li key={t.id} data-testid={`timeline-${t.kind}`} className="flex gap-3 py-2 border-b border-[#161616] last:border-0">
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tint}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-200 leading-snug">{t.text}</div>
                  <div className="text-[10px] font-mono text-slate-600 mt-0.5">{clock(t.created_at)}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
